import { MediaSide, Product } from '../../../eizot/types'
import { EizoTPreview } from '../../../preview/pixi-main'
import configs from '../../../print/config'
import { Transform } from '../../../preview/pixi-impl'
import { Item } from './ItemConfig'

export async function intoPreview(
  product: Product,
  item: Item,
  renderConfig: {
    skipSizeScale?: boolean
    includedSizes?: string[]
  } = {}
): Promise<EizoTPreview> {
  const config = configs[item.type] ?? configs.default

  const mainImage =
    product.thumb_time == null
      ? `/api/media/${product.media.id}/screencap`
      : `/api/media/${product.media.id}/screencap?seconds=` + product.thumb_time
  let defaultSide = item.media_side === MediaSide.BACK ? '105' : '103'

  const isTote = item.type.startsWith('778-TCC')

  if (isTote /* canvas tote */) {
    defaultSide = item.media_side === MediaSide.BACK ? '2' : '1'
  }

  const preview = new EizoTPreview(
    config,
    (type, color_id) => {
      const urls: Record<string, string> = {}
      config.sides.forEach((side) => {
        urls[side.name] = makeImageLink(type, side.name, color_id)
      })
      return urls
    },
    renderConfig.includedSizes
  )

  const color = item.colors[0]
  preview.setColor(color.system_id, color.uses_dark)

  for (const size of item.sizes) {
    preview.addSize(size.name)
    preview.setSize(size.name, renderConfig.skipSizeScale)

    await preview.setMainLayer({
      url: mainImage,
      scale: size.scale,
      left: size.offset_x,
      top: size.offset_y,
      side: isTote ? '1' : '103',
    })

    await preview.setBackLayer({
      url: mainImage,
      scale: size.scale_back ?? 1,
      left: size.offset_back_x ?? 0,
      top: size.offset_back_y ?? 0,
      side: isTote ? '2' : '105',
    })

    preview.setPrintSide(defaultSide)

    for (const mask of size.masks ?? []) {
      await preview.setMask(mask.side, mask)
    }

    await Promise.all(
      size.layers.map(async (layer) => {
        const logoLayer = await preview.addLogo(layer.location, layer)
        return { logo: layer.logo, layer: logoLayer }
      })
    )
  }

  if (item.timestamp != null) {
    await setupTimestamp(preview, item.timestamp)
  }

  if (item.numbering != null) {
    await setupNumbering(preview, item.numbering)
  }

  preview.setSize(item.sizes[0]?.name ?? 'S')
  preview.blend(!!color.blend_mode)
  return preview
}

export async function setupTimestamp(
  preview: EizoTPreview,
  data: Product['items'][number]['timestamp']
) {
  const tl = await preview.setTimestamp(data.location, {
    ...data,
    font: {
      family: data.font.split('.')[0],
      url: `/assets/fonts/${data.font}`,
    },
  })
  tl.applyTransform(new Transform(data.offset_y, data.offset_x, 1, 1))
}

export async function setupNumbering(
  preview: EizoTPreview,
  data: Product['items'][number]['numbering']
) {
  const nl = await preview.setNumbering(data.location, {
    ...data,
    text_font: {
      family: data.text_font.split('.')[0],
      url: `/assets/fonts/${data.text_font}`,
    },
    number_font: {
      family: data.number_font.split('.')[0],
      url: `/assets/fonts/${data.number_font}`,
    },
  })

  nl.applyTransform(new Transform(data.offset_y, data.offset_x, 1, 1))
}

function makeImageLink(type: string, side: string, color_id: string) {
  return `/storage/print/${type}_${side}_${color_id}.png?v1`
}
